import { useEffect, useRef, useState } from "react";
import { lighten } from "polished";
import { Chart } from "chart.js";


export const ChartPiePC = (props) => {
  const { datasets } = props;

  const canvas = useRef(null);
  const chart = useRef(null);
  const [itemHover, setItemHover] = useState(null)

  useEffect(() => {
    const onHoverHandler = (event, elements) => {
      const newHoverIndex = elements.length > 0 ? elements[0].index : null;
      setItemHover(newHoverIndex);
    };

    const chartOptions = {
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      },
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 3,
      animation: false,
      onHover:onHoverHandler,
      layout: {
        padding: {
          top: 10,
          right: 10,
          bottom: 10,
          left: 10
        }
      }
    };

    const currentDataset = datasets.find(data => data.name === "currentData");
    const data = {
      labels: currentDataset.dataset.map(item => item.label.split("-").slice(0, -1).join("-")),
      datasets: [{
        data: currentDataset.dataset.map(item => item.data.reduce((acc, curr) => acc + curr, 0)),
        idData: currentDataset.dataset.map((data, index) => index),
        backgroundColor: currentDataset.dataset.map(item => item.backgroundColor),
        hoverBackgroundColor: currentDataset.dataset.map(item => lighten(0.1, item.backgroundColor)),
        borderWidth: 2, ///spaziatura tra gli spicchi
        hoverOffset: 20 //spostamento spicchio con l'hover
      }]
    };

    if (chart.current) {
      chart.current.destroy();
    }

    chart.current = new Chart(canvas.current, {
      type: 'pie',
      data,
      options: chartOptions,
      plugins: [{
        id: 'customTooltip',
        afterDraw: (chart) => externalTooltipHandler(chart, itemHover, null)
      }]
    });

    return () => chart.current?.destroy();
    // eslint-disable-next-line
  }, [datasets, itemHover]);

  // Tooltip personalizzato come plugin
  const externalTooltipHandler = (chart) => {
    const ctx = chart.ctx;
    const fontSize = 16;
    ctx.save();
    ctx.font = `${fontSize}px Arial`;

    const tooltipArrays = {
      topLeft: [],
      topRight: [],
      bottomLeft: [],
      bottomRight: []
    };

    chart.data.datasets.forEach((dataset, i) => {
      const total = dataset.data.reduce((sum, value) => sum + value, 0);
      const backgroundColors = dataset.backgroundColor;
      const meta = chart.getDatasetMeta(i);
      const idData = dataset.idData;

      meta.data.forEach((sector, index) => {
        const { x, y, startAngle, endAngle, outerRadius } = sector;
        const midAngle = (startAngle + endAngle) / 2;
        const isLeft = Math.cos(midAngle) < 0;
        const isTop = Math.sin(midAngle) < 0;
        const text = `${chart.data.labels[index]}: ${(dataset.data[index]).toFixed(1)} KWh (${(dataset.data[index] / total * 100).toFixed(2)}%)`;
        const textColor = getContrastColor(backgroundColors[index]);
        const tooltipInfo = {
          idData:idData[index],
          text,
          x,
          y,
          midAngle,
          outerRadius,
          color: backgroundColors[index],
          textColor
        };

        if (isLeft && isTop) {
          tooltipArrays.topLeft.push(tooltipInfo);
        } else if (!isLeft && isTop) {
          tooltipArrays.topRight.push(tooltipInfo);
        } else if (isLeft && !isTop) {
          tooltipArrays.bottomLeft.push(tooltipInfo);
        } else {
          tooltipArrays.bottomRight.push(tooltipInfo);
        }
      });
    });

    Object.keys(tooltipArrays).forEach(quadrant => {
      drawTooltips(ctx, tooltipArrays[quadrant], quadrant, chart);
    });

    ctx.restore();
  };

  const drawTooltips = (ctx, tooltips, quadrant, chart) => {
    const numCostant = 16; /// numero costante che regola le dimenzioni generali dei tooltip
    const padding = 5; // padding del grafico in generale
    let startY = padding; // spaziatura in alto e in basso dei tooltip
    let increment = numCostant + 20; // spaziatura tra i tooltip

    // Se i tooltip sono a sinistra, inverto l'ordine di rendering partendo dal basso
    if (quadrant === 'topLeft' || quadrant === 'bottomRight') {
      tooltips = tooltips.reverse();
    }

    tooltips.forEach((tooltip, index) => {
      const { x, y, idData, midAngle, outerRadius, text, textColor, color } = tooltip;
      const hoverEvent = (idData === itemHover) ? true : false; //se è attivo l'hover in base alla corrispondenza dell'id
      const lineWidth = hoverEvent ? 4 : 2;
      const textWidth = ctx.measureText(text).width + padding * lineWidth;
      const textHeight = (numCostant + padding * (hoverEvent ? 3 : 2));
      let xPosition, yPosition;
      // Imposta la larghezza della linea solo se l'indice corrisponde a quello in hover

      switch (quadrant) {
        case 'topLeft':
          xPosition = padding;
          yPosition = startY + (increment * index);
          break;
        case 'topRight':
          xPosition = chart.width - textWidth - padding;
          yPosition = startY + (increment * index);
          break;
        case 'bottomLeft':
          xPosition = padding;
          yPosition = chart.height - startY - (increment * index) - textHeight;
          break;
        case 'bottomRight':
          xPosition = chart.width - textWidth - padding;
          yPosition = chart.height - startY - (increment * index) - textHeight;
          break;
        default: 
          break;
      }
    
    // linee
    // calcola il punto finale per le linee
      if (hoverEvent) {
        const lineEndX = isLeft(quadrant) ? xPosition + textWidth : xPosition; 
        const lineEndY = yPosition + textHeight -2; // il -2 indica dove piazzare nel lato del tooltip la linea
        ctx.beginPath();
        ctx.moveTo(lineEndX, lineEndY); //movimento linea da punto x a y
        ctx.lineTo(x + Math.cos(midAngle) * outerRadius, y + Math.sin(midAngle) * outerRadius); // calcolo inizio linea
        ctx.strokeStyle = hoverEvent ? lighten(0.1, color) : color; //colore linea
        ctx.lineWidth = lineWidth; // spessore linea
        ctx.stroke();
      }

      // background del tooltip
      ctx.fillStyle = hoverEvent ? lighten(0.1, color) : color;;
      ctx.fillRect(xPosition, yPosition, textWidth, textHeight);

      //testo 
      const textX = xPosition + (textWidth - ctx.measureText(text).width) / 2;
      const textY = yPosition + ((textHeight + numCostant) / 2) - 4;
      ctx.fillStyle = textColor;
      ctx.fillText(text, textX, textY);
    });
  };
  ///serve per il contrasto tra testo e sfondo tooltip
  ///con contrast si determina quanto deve essere sensibile e in caso
  /// vado a colorare il testo di nero 
  const getContrastColor = (backgroundColor) => {
    const contrast = 160
    const r = parseInt(backgroundColor.slice(1, 3), 16);
    const g = parseInt(backgroundColor.slice(3, 5), 16);
    const b = parseInt(backgroundColor.slice(5, 7), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) > contrast ? '#000000' : '#FFFFFF';
  };

  function isLeft(quadrant) {
    return quadrant === 'topLeft' || quadrant === 'bottomLeft';
  }

  return (
    <canvas ref={canvas} />
  );
}
